import React from 'react'
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  CssBaseline,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LazyMotion } from 'motion/react'

const loadMotionFeatures = () =>
  import('../motion/features').then((res) => res.default)

const BLACK = '#101117'
const CREAM = '#FFF9F3'

const MONO_FONT_FAMILY = [
  '"JetBrains Mono"',
  'Menlo',
  'Consolas',
  'Courier New',
  'monospace',
].join(',')

const ROBOTO_MONO_FONT_FAMILY = [
  '"Roboto Mono"',
  'Menlo',
  'Consolas',
  'Courier New',
  'monospace',
].join(',')

const COMMUNITY_GOTHIC_FONT_FAMILY = [
  'CommunityGothic',
  '"CommunityGothic Fallback"',
  'BlinkMacSystemFont',
  '"Helvetica Neue"',
  '"Segoe UI"',
  'Arial',
  '-apple-system',
  'Roboto',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',')

const theme = createTheme({
  palette: {
    neutral: {
      gray: '#4A4A4A',
      mediumGray: '#9D9D9D',
      lightGray: '#E5E5E5',
      secondaryBackground: CREAM,
    },
    common: { black: BLACK },
    primary: { main: '#209F5B', light: '#23CB6E', dark: '#1B854D' },
    secondary: { main: '#209F5B' },
    success: { main: '#3AB47E' },
    info: { main: '#5295FF' },
    warning: { main: '#F9D724' },
    error: { main: '#FB4F2F' },
    background: { default: '#fff' },
    text: { primary: BLACK },
    inverse: { main: '#fff', contrastText: BLACK },
  },
  app: {
    // Brand accents — Design System v2
    neon: '#67FF64', // a.k.a. Lime (dark-surface accent only)
    tomato: '#FB4F2F',
    aqua: '#5295FF',
    purple: '#7C5CFC',
    greenHover: '#1B854D',
    greenDark: '#0E5C36',
    // Pastel chip tokens (legacy — chip backgrounds)
    green: '#C5E9C7',
    yellow: '#F9D99E',
    purplePastel: '#DAC2E8',
    primaryGradient:
      'linear-gradient(109.62deg, #FC642D 16.67%, #FFC371 108.83%)',
    secondaryGradient: BLACK,
    monoFontFamily: MONO_FONT_FAMILY,
    robotoMonoFontFamily: ROBOTO_MONO_FONT_FAMILY,
  },
  typography: {
    fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
    // Design System v2 — display headline (cover/hero)
    t1: {
      fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
      fontSize: 'clamp(3rem, 7vw, 6rem)',
      fontWeight: 900,
      lineHeight: 0.92,
      letterSpacing: '-0.035em',
      textTransform: 'uppercase',
    },
    // Kicker (mono eyebrow label)
    t2: {
      fontFamily: MONO_FONT_FAMILY,
      fontSize: '0.6875rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
    },
    // Section heading
    h1: {
      fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
      fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
      fontWeight: 900,
      lineHeight: 1,
      letterSpacing: '-0.025em',
      textTransform: 'uppercase',
    },
    // Sub-heading
    h2: {
      fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
      fontSize: 'clamp(1.5rem, 3.5vw, 2.375rem)',
      fontWeight: 900,
      lineHeight: 1.08,
      letterSpacing: '-0.04em',
    },
    h3: {
      fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
      fontSize: '1.25rem',
      fontWeight: 900,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
      fontSize: '1.125rem',
      fontWeight: 700,
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.25,
    },
    h6: {
      fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1.25,
    },
    // Lede (oversized intro paragraph)
    body2: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
    body3: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.55 },
    body4: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
    button: {
      fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: 'normal',
      textTransform: 'none',
    },
    buttonSmall: {
      fontFamily: COMMUNITY_GOTHIC_FONT_FAMILY,
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: 'normal',
      textTransform: 'none',
    },
    caption1: {
      fontWeight: 700,
      fontSize: '0.75rem',
      lineHeight: 1.25,
      letterSpacing: '0.05em',
    },
    caption2: {
      fontFamily: MONO_FONT_FAMILY,
      fontWeight: 700,
      fontSize: '0.625rem',
      lineHeight: 1.25,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
    },
    body1: {},
    caption: {},
    subtitle2: {},
    overline: {},
  },
  mixins: {
    constrainWidth: {
      width: '100%',
      maxWidth: 1370,
      margin: '0 auto',
      padding:
        '0 calc(env(safe-area-inset-right) + 20px) 0 calc(env(safe-area-inset-left) + 20px);',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, h1, h2, h3, p, ul': { margin: 0, padding: 0 },
        'html, body, #root': { width: '100%', height: '100%' },
        // Offset anchor jumps by fixed AppBar height (Layout APP_BAR_HEIGHT
        // desktop / APP_BAR_MOBILE_HEIGHT below the mobile nav breakpoint).
        html: { scrollPaddingTop: '80px' },
        '@media (max-width:1150px)': { html: { scrollPaddingTop: '64px' } },
        button: { fontFamily: 'inherit' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 8, borderRadius: 5 },
        bar: { borderRadius: 5 },
      },
    },
    MuiTab: { styleOverrides: { root: { opacity: 1 } } },
    MuiRadio: {
      defaultProps: { color: 'secondary', sx: { color: 'secondary.main' } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, variant: 'contained' },
      styleOverrides: {
        root: {
          borderRadius: 3,
          whiteSpace: 'nowrap',
          transition: 'background-color .15s, color .15s, border-color .15s',
        },
        sizeSmall: { padding: '8px 16px' },
        sizeMedium: { padding: '13px 26px' },
        sizeLarge: { padding: '18px 36px' },
        // Text button → Design System v2 `.btn-link`: green label with a
        // 2px green underline, no surface fill. `color="inverse"` flips it to
        // white for use on dark sections. Bottom padding is reduced by 2px
        // so the 2px underline border doesn't add to outer height. Nested
        // size selectors are needed to beat the root sizeMedium specificity.
        text: ({ ownerState, theme }) => {
          const fg =
            ownerState.color === 'inverse' ? '#fff' : theme.palette.primary.main
          const hover =
            ownerState.color === 'inverse' ? '#fff' : theme.palette.primary.dark
          return {
            background: 'none',
            color: fg,
            borderBottom: `2px solid ${fg}`,
            borderRadius: 0,
            '&.MuiButton-sizeSmall': { padding: '8px 0 6px' },
            '&.MuiButton-sizeMedium': { padding: '13px 0 11px' },
            '&.MuiButton-sizeLarge': { padding: '18px 0 16px' },
            '&:hover, &:focus-visible': {
              background: 'none',
              color: hover,
              borderBottomColor: hover,
            },
          }
        },
        // Outlined → Design System v2 `.btn-secondary`: charcoal label, 2px
        // charcoal border; inverts to a filled charcoal surface on hover.
        // Default rests on a white fill so the button reads as a card on
        // colored section backgrounds. `color="inverse"` (dark sections)
        // stays transparent and flips the border + label to white. Padding
        // is reduced by 2px on each side to compensate for the 2px border so
        // outlined matches contained's outer dimensions. Nested size
        // selectors are needed to beat the root sizeMedium specificity.
        outlined: ({ ownerState, theme }) => {
          const isInverse = ownerState.color === 'inverse'
          const fg = isInverse ? '#fff' : theme.palette.common.black
          return {
            background: isInverse ? 'transparent' : theme.palette.common.white,
            color: fg,
            borderColor: fg,
            borderWidth: 2,
            '&.MuiButton-sizeSmall': { padding: '6px 14px' },
            '&.MuiButton-sizeMedium': { padding: '11px 24px' },
            '&.MuiButton-sizeLarge': { padding: '16px 34px' },
            '&:hover, &:focus-visible': {
              borderWidth: 2,
              borderColor: fg,
              background: fg,
              color: isInverse ? '#000' : '#fff',
            },
          }
        },
        // Contained → Design System v2:
        //   primary  = `.btn-primary` (green fill, white text)
        //   secondary = `.btn-dark`  (charcoal fill, white text)
        //   inverse  = white fill, charcoal text — for use on dark sections.
        contained: ({ ownerState, theme }) => {
          const bg =
            ownerState.color === 'secondary'
              ? theme.palette.common.black
              : ownerState.color === 'inverse'
                ? '#fff'
                : theme.palette.primary.main
          const fg =
            ownerState.color === 'inverse' ? theme.palette.common.black : '#fff'
          const hoverBg =
            ownerState.color === 'secondary'
              ? '#000'
              : ownerState.color === 'inverse'
                ? '#f1f1f1'
                : theme.app.greenHover
          return {
            border: 'none',
            background: bg,
            color: fg,
            '&:hover, &:focus-visible': { background: hoverBg, color: fg },
          }
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.4)' } },
    },
    MuiAppBar: { styleOverrides: { root: { boxShadow: 'none' } } },
    MuiList: {
      styleOverrides: { root: { minWidth: 250 } },
      defaultProps: { dense: true },
    },
    MuiMenu: {
      styleOverrides: {
        list: { padding: 0 },
        paper: ({ theme }) => ({
          border: `1px solid ${theme.palette.common.black}`,
          padding: 20,
          boxShadow: 'none',
        }),
        root: { padding: 2 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.button,
          padding: 8,
          backgroundClip: 'content-box',
          '&:hover': {
            backgroundClip: 'content-box',
            backgroundColor: theme.app.neon,
          },
          //   background: theme.palette.neutral.secondaryBackground,
          // },
          // '&.Mui-selected, &.Mui-selected:hover': {
          //   background: `${theme.palette.neutral.secondaryBackground} !important`,
          // },
        }),
      },
    },
    MuiLink: { defaultProps: { underline: 'hover', color: 'secondary.main' } },
    MuiButtonBase: {
      defaultProps: { disableRipple: true, disableTouchRipple: true },
    },
    MuiSwitch: { defaultProps: { disableFocusRipple: true } },
  },
})

/**
 * Display/heading sizes use clamp() for fluid responsive scaling,
 * so they no longer need explicit per-breakpoint overrides.
 */
export const ThemeProvider: React.FunctionComponent<{
  children: React.ReactNode
}> = ({ children }) => {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <LazyMotion features={loadMotionFeatures} strict>
          {children}
        </LazyMotion>
      </LocalizationProvider>
    </MuiThemeProvider>
  )
}
